import { PlanCode, SupportedCountryCodes } from '../enums.js'
import { translate } from '../i18n.js'
import { roundPrice, roundUnitPrice } from './maths.js'

export const formatMonthlyPrice = (price: number, currencySymbol: string, locale: string) => {
    return `${roundPrice(price).toFixed(2)} ${currencySymbol}/${translate('currency.perMonth', { lng: locale })}`
}

export const formatPrice = (price: number, currencySymbol: string) => {
    return `${roundPrice(price).toFixed(2)} ${currencySymbol}`.replace(/ /g, '\u00A0')
}

export const arrayUnique = <T>(array: T[]): T[] => {
    return [...new Set(array)]
}

export const getPlanDurationInMonths = (duration: number, plan?: PlanCode, countryCode?: SupportedCountryCodes) => {
    if (plan === PlanCode.VALUE && countryCode === SupportedCountryCodes.FINLAND) {
        return 36
    }

    return duration
}

/**
 * Show 3 decimal places, or 2 if the last digit would be 0.
 * Examples:
 *     7.13426 -> 7.134
 *     7.13018 -> 7.13
 *     7.13985 -> 7.14
 *     7.1     -> 7.10
 *     7       -> 7.00
 */
export const formatUnitPrice = (price: number, currencySymbol: string) => {
    let rounded = roundUnitPrice(price).toFixed(3)

    if (rounded.endsWith('0')) {
        rounded = rounded.slice(0, -1)
    }

    return `${rounded} ${currencySymbol}`.replace(/ /g, '\u00A0')
}

export const errorHasPayload = (error: unknown): error is { payload: Record<string, unknown> } => {
    return !!(error as { payload: Record<string, unknown> }).payload
}

export const errorHasMessage = (error: unknown): error is { message: string } => {
    return !!(error as { message: string }).message
}

export type ResultOrError<T> = { ok: true; result: T } | { ok: false; error: string }

/**
 * Sanitizes headers by removing sensitive information.
 * @param headers - The raw request/response headers.
 * @returns A clean version of the headers.
 */
export const redactHeaders = (headers: Record<string, string | string[] | undefined>) => {
    const delicateHeaders = [
        'authorization',
        'proxy-authorization',
        'cookie',
        'set-cookie',
        'x-api-key',
        'x-amz-security-token',
        'cf-access-jwt-assertion',
        'php-auth-pw', // Old school but still exists
        'surrogate-key', // Fastly/CDN specific
    ]

    return Object.keys(headers).reduce(
        (acc, key) => {
            const lowerKey = key.toLowerCase()

            if (!delicateHeaders.includes(lowerKey)) {
                acc[key] = headers[key]
            }

            return acc
        },
        {} as Record<string, string | string[] | undefined>,
    )
}
